// 介錯の「執行」の炎と「発勁」の輪を、手描きの絵で描く版 (2026-10-20・開発版の既定・DEV `?execFlame=sprite`)。
//
// 作者 (10-20): 「他のエフェクトをお手本通り作ってくれてるエフェクトみたいに、画像も取り入れたらもっと良くなるんじゃないのかな」。
//   前のコードの版は「炎がいっぱい付いてて気持ち悪い」「全部プログラムの描画で書いているのが丸分かり」。
// 分け方: 炎・輪・芯の質感は Codex が描いた絵 (黒背景・加算で黒を消す・public/fx/exec/)。
//   出る・広がる・消える・刃に沿って置く・背景の歪み・瓦礫・土ぼこりはコードのまま (exec_fx.ts / fajin_fx.ts)。
// 絵の元 = そざい/concept/exec-fx-1020/sprites/ (票 exec-fx-sprites-1020)。切り出し = scripts/slice_exec_fx_sprites.py。
// 絵が読めていない間 (読み込み中・Vitest) は null を返し、呼ぶ側はコードの版で描く。

type Frame = { img: HTMLImageElement | null }
type Sheet = { names: readonly string[]; frames: Frame[] }

function sheet(names: readonly string[]): Sheet { return { names, frames: names.map(() => ({ img: null })) } }

// 刃に灯る炎の舌 4つ (大・中・小・二股) × 揺らぎ3コマ。根元 = 絵の下辺から 2.5% 上の真ん中。
export const FLAME_KINDS = 4
export const FLAME_FRAMES = 3
const flames = sheet(Array.from({ length: FLAME_KINDS * FLAME_FRAMES }, (_, i) => `flame_${Math.floor(i / FLAME_FRAMES)}_${i % FLAME_FRAMES}`))
// 床を打った所の4段 (噴く・這う・ちろちろ・燃え残り)。打点 = 絵の下辺から 2% 上の真ん中。
export const BURST_FRAMES = 4
const bursts = sheet(Array.from({ length: BURST_FRAMES }, (_, i) => `burst_${i}`))
// 発勁の三日月の輪 3形。膨らみは絵の左。輪の中心 = 絵の真ん中。
export const RING_KINDS = 3
const rings = sheet(Array.from({ length: RING_KINDS }, (_, i) => `ring_${i}`))
// 発勁の先頭の芯 4コマ。先端は絵の左端から 4.5%、高さの真ん中。
export const FRONT_FRAMES = 4
const fronts = sheet(Array.from({ length: FRONT_FRAMES }, (_, i) => `front_${i}`))

const ALL = [flames, bursts, rings, fronts]
export const FLAME_ANCHOR_Y = 0.975 // 切り出しで根元をそろえた (395px の台・下 10px)
export const BURST_ANCHOR_Y = 0.98 // 同 (520px の台・下 10px)
export const FRONT_TIP_X = 0.045

let started = false
/** 執行・発勁の絵の読み込みを始める (render の preload から1回)。 */
export function preloadExecFxSprites(prefix = '/fx/exec/'): void {
  if (started || typeof Image === 'undefined') return
  started = true
  for (const s of ALL) {
    s.names.forEach((name, i) => {
      const img = new Image()
      img.onload = () => { s.frames[i]!.img = img }
      img.src = `${prefix}${name}.png`
    })
  }
}

function ready(s: Sheet): boolean { return s.frames.every(f => f.img !== null) }
export function execFlameSpritesReady(): boolean { return ready(flames) && ready(bursts) }
export function fajinSpritesReady(): boolean { return ready(rings) && ready(fronts) }

export function flameSprite(kind: number, frame: number): HTMLImageElement | null {
  const k = ((kind % FLAME_KINDS) + FLAME_KINDS) % FLAME_KINDS
  const f = ((frame % FLAME_FRAMES) + FLAME_FRAMES) % FLAME_FRAMES
  return flames.frames[k * FLAME_FRAMES + f]!.img
}
export function burstSprite(i: number): HTMLImageElement | null { return bursts.frames[Math.max(0, Math.min(BURST_FRAMES - 1, i))]!.img }
export function ringSprite(i: number): HTMLImageElement | null { return rings.frames[((i % RING_KINDS) + RING_KINDS) % RING_KINDS]!.img }
export function frontSprite(i: number): HTMLImageElement | null { return fronts.frames[((i % FRONT_FRAMES) + FRONT_FRAMES) % FRONT_FRAMES]!.img }

/** 黒背景の絵を加算で置く。(ax, ay) = 絵の中の基準点 (0..1)。flipX で左右を返す。 */
export function drawAdditive(
  ctx: CanvasRenderingContext2D, img: HTMLImageElement | HTMLCanvasElement, x: number, y: number, w: number, h: number,
  ax: number, ay: number, alpha: number, rot = 0, flipX = false,
): void {
  if (alpha <= 0.01 || w <= 0.5 || h <= 0.5) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.translate(x, y)
  if (rot) ctx.rotate(rot)
  if (flipX) ctx.scale(-1, 1)
  ctx.drawImage(img, -w * ax, -h * ay, w, h)
  ctx.restore()
}
